import { z } from "zod";
import { IdSchema, IsoDateTimeSchema } from "./primitives.js";
/**
 * Explicit clinic assistance requests persist independently of visit completion. A request
 * filed from Attention may have no visit behind it, may name who should pick it up, and may
 * say by when.
 */
export const StaffRequestSchema = z
    .object({
    id: IdSchema,
    conversationId: IdSchema.optional(),
    siteId: IdSchema,
    patientId: IdSchema,
    encounterId: IdSchema.optional(),
    appointmentId: IdSchema.optional(),
    displayName: z.string().min(1),
    roomName: z.string().optional(),
    requestText: z.string().trim().min(1).max(2000),
    /** Attention alerts belong in the recipient inbox; encounter requests stay on Today. */
    source: z.enum(["encounter", "attention"]).optional(),
    status: z.enum(["open", "in_progress", "completed", "cancelled"]),
    version: z.number().int().positive(),
    createdAt: IsoDateTimeSchema,
    createdBy: IdSchema,
    requestedBy: z.string().min(1),
    assignedTo: IdSchema.optional(),
    assignedToName: z.string().optional(),
    dueAt: IsoDateTimeSchema.optional(),
    completedAt: IsoDateTimeSchema.optional(),
    /**
     * A referral package handed to staff on approval (REF-5): the sealed
     * artifact is attached by reference and opened through the same
     * role-gated prepared-PDF route the tracker uses.
     */
    referralId: IdSchema.optional(),
    referralArtifactId: IdSchema.optional(),
    referralArtifactVersion: z.number().int().positive().optional(),
    referralPdfPath: z.string().startsWith("/v1/referrals/").max(500).optional(),
    /** The result or incoming report the request is about, so its holder can open and work it. */
    resultId: IdSchema.optional(),
    reportId: IdSchema.optional(),
})
    .passthrough();
export const StaffRequestsSchema = z.array(StaffRequestSchema);
export const StaffRequestsQuerySchema = z.object({ siteId: IdSchema }).strict();
export const CreateStaffRequestRequestSchema = z
    .object({
    siteId: IdSchema,
    patientId: IdSchema,
    requestText: z.string().trim().min(1).max(2000),
    idempotencyKey: IdSchema,
})
    .strict();
/** A request for a patient, filed under a visit only when one is known. */
export const CreatePatientStaffRequestRequestSchema = CreateStaffRequestRequestSchema.extend({
    encounterId: IdSchema.nullable().optional(),
    assignedTo: IdSchema.nullable().optional(),
    dueAt: IsoDateTimeSchema.nullable().optional(),
    /** At most one: the result or the incoming report the request is about. */
    resultId: IdSchema.nullable().optional(),
    reportId: IdSchema.nullable().optional(),
}).strict();
export const TransitionStaffRequestRequestSchema = z
    .object({
    siteId: IdSchema,
    expectedVersion: z.number().int().positive(),
    targetStatus: z.enum(["in_progress", "completed", "cancelled"]),
})
    .strict();
/** Name who handles a request; null clears the assignee. */
export const AssignStaffRequestRequestSchema = z
    .object({
    siteId: IdSchema,
    expectedVersion: z.number().int().positive(),
    assignedTo: IdSchema.nullable(),
})
    .strict();
/** The open request handing a result or report to staff, shown on the work item it is about. */
export const StaffHandoffSummarySchema = z
    .object({
    requestId: IdSchema,
    status: z.enum(["open", "in_progress"]),
    version: z.number().int().positive(),
    assignedTo: IdSchema.optional(),
    assignedToName: z.string().min(1).max(240).optional(),
})
    .strict();
export const SiteStaffMemberSchema = z
    .object({
    userId: IdSchema,
    displayName: z.string().min(1),
    role: z.string().min(1),
})
    .strict();
export const SiteStaffListSchema = z
    .object({ siteId: IdSchema, members: z.array(SiteStaffMemberSchema) })
    .strict();
//# sourceMappingURL=staff-requests.js.map